import React from 'react';
import {
  Box, Button, ButtonText, Center, HStack, Icon, Text, VStack,
} from '@gluestack-ui/themed';
import {
  Archive, Camera, ClipboardCheck,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

export type ConsentStepProps = {
  onAccept: () => void;
  isSubmitting?: boolean;
  policyVersion?: string;
};

const PURPOSE_ROWS = [
  { key: 'recorded', icon: Camera, testId: 'oral-exam-consent-purpose-recorded' },
  { key: 'proctorGrade', icon: ClipboardCheck, testId: 'oral-exam-consent-purpose-proctor' },
  { key: 'appealRecord', icon: Archive, testId: 'oral-exam-consent-purpose-appeal' },
] as const;

/** Recorded GDPR consent screen (student UX §3.2 v1.3 — three purpose icon rows). */
export function ConsentStep({
  onAccept,
  isSubmitting = false,
  policyVersion,
}: ConsentStepProps) {
  const { t } = useTranslation();

  return (
    <Center flex={1} p="$6" testID="oral-exam-consent-step">
      <VStack space="lg" maxWidth={560} width="100%" alignItems="center">
        <Box
          w={72}
          h={72}
          borderRadius="$full"
          bg="$bgBrandSubtle"
          borderWidth={2}
          borderColor="$borderBrand"
          alignItems="center"
          justifyContent="center"
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        >
          <Box w={36} h={36} borderRadius="$full" bg="$brandBase" opacity={0.85} />
        </Box>

        <VStack space="sm" alignItems="center" width="100%">
          <Text size="xl" fontWeight="semibold" color="$textHeading" textAlign="center">
            {t('oralExam.consent.title')}
          </Text>
          <Box
            width="100%"
            bg="$bgSubtle"
            borderRadius="$lg"
            borderWidth={1}
            borderColor="$borderLight"
            p="$4"
            testID="oral-exam-consent-body"
          >
            <VStack space="md">
              {PURPOSE_ROWS.map(({ key, icon: PurposeIcon, testId }) => (
                <HStack key={key} space="sm" alignItems="center" testID={testId}>
                  <Icon as={PurposeIcon} size="sm" color="$brandBase" />
                  <Text size="md" color="$textBody" flex={1} lineHeight="$lg">
                    {t(`oralExam.consent.purpose.${key}`)}
                  </Text>
                </HStack>
              ))}
              {policyVersion ? (
                <Text size="sm" color="$textSubtle" lineHeight="$md">
                  {t('oralExam.consent.retention', { policyVersion })}
                </Text>
              ) : null}
            </VStack>
          </Box>
        </VStack>

        <VStack space="sm" width="100%" alignItems="center">
          <Button
            size="md"
            onPress={onAccept}
            isDisabled={isSubmitting}
            testID="oral-exam-consent-accept"
            width="100%"
            maxWidth={280}
          >
            <ButtonText>{t('oralExam.consent.accept')}</ButtonText>
          </Button>
        </VStack>
      </VStack>
    </Center>
  );
}
